import { useEffect, useRef, useState, type ReactNode } from "react";

// Floating section navigator — opens a slide-in panel from the right with a
// list of jump-to links. Active section is tracked via IntersectionObserver
// so the user always knows where they are. Sections that aren't rendered
// (e.g. MerchSection when empty) are filtered out automatically.

export interface SectionNavItem {
  id: string;
  label: string;
  icon?: ReactNode;
}

interface Props {
  sections: SectionNavItem[];
}

export function SectionNav({ sections }: Props) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [availableIds, setAvailableIds] = useState<Set<string>>(new Set());

  // Re-check periodically which sections are actually in the DOM. Sections
  // that don't render (e.g. merch when empty, ratings when book has none) get
  // filtered out of the nav.
  useEffect(() => {
    const check = () => {
      const present = new Set<string>();
      for (const s of sections) {
        if (document.getElementById(s.id)) present.add(s.id);
      }
      setAvailableIds((prev) => {
        if (prev.size === present.size && [...prev].every((id) => present.has(id))) return prev;
        return present;
      });
    };
    check();
    const interval = setInterval(check, 1500);
    return () => clearInterval(interval);
  }, [sections]);

  // Track which section is currently in view.
  useEffect(() => {
    if (availableIds.size === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost visible entry — that's what the user is reading.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0 && visible[0]) {
          setActive(visible[0].target.id);
        }
      },
      // Trigger when section header is in the top third of the viewport.
      { rootMargin: "-25% 0px -55% 0px", threshold: 0 }
    );
    for (const id of availableIds) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [availableIds]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  function scrollTo(id: string) {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  }

  const visibleSections = sections.filter((s) => availableIds.has(s.id));
  if (visibleSections.length === 0) return null;

  return (
    <>
      {/* FAB — always visible, even on desktop. Lives at bottom-right. */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open section navigation"
        className={[
          "fixed bottom-5 right-5 z-30",
          "w-14 h-14 rounded-full",
          "bg-gradient-to-br from-blush to-rose text-white",
          "shadow-[0_6px_16px_rgba(199,123,133,0.45),_0_2px_4px_rgba(61,43,61,0.10)]",
          "flex items-center justify-center",
          "transition-all duration-200 ease-out",
          "hover:scale-110 hover:shadow-[0_8px_24px_rgba(199,123,133,0.55)]",
          "active:scale-95",
          open ? "opacity-0 pointer-events-none" : "opacity-100",
        ].join(" ")}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="5" cy="6.5" r="1.4" fill="currentColor" />
          <circle cx="5" cy="12" r="1.4" fill="currentColor" />
          <circle cx="5" cy="17.5" r="1.4" fill="currentColor" />
          <path
            d="M10 6.5h10M10 12h10M10 17.5h7"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* Backdrop */}
      <div
        onClick={() => setOpen(false)}
        className={[
          "fixed inset-0 z-40 bg-plum/30 backdrop-blur-sm transition-opacity duration-300",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        ].join(" ")}
      />

      {/* Sliding panel */}
      <aside
        className={[
          "fixed top-0 bottom-0 right-0 z-50",
          "w-[88vw] max-w-sm",
          "bg-cream-50 border-l border-cream-200",
          "shadow-[-12px_0_40px_rgba(61,43,61,0.15)]",
          "flex flex-col",
          "transition-transform duration-300 ease-out",
          open ? "translate-x-0" : "translate-x-full",
        ].join(" ")}
      >
        <div className="flex items-center justify-between p-5 border-b border-cream-200">
          <div>
            <div className="text-[0.65rem] uppercase tracking-[0.22em] text-gold mb-0.5">
              Jump to
            </div>
            <h3 className="font-serif text-xl text-plum">Sections</h3>
          </div>
          <button
            onClick={() => setOpen(false)}
            aria-label="Close navigation"
            className="text-plum-soft hover:text-rose p-2 -mr-2 rounded-full hover:bg-cream-100"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3">
          <ul className="flex flex-col gap-0.5">
            {visibleSections.map((s) => {
              const isActive = active === s.id;
              return (
                <li key={s.id}>
                  <button
                    onClick={() => scrollTo(s.id)}
                    className={[
                      "w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl",
                      "text-[0.9375rem] transition-all duration-150",
                      isActive
                        ? "bg-rose text-white font-medium shadow-[0_2px_8px_rgba(199,123,133,0.30)]"
                        : "text-plum hover:bg-cream-100",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "shrink-0 w-5 h-5 flex items-center justify-center",
                        isActive ? "text-white" : "text-gold",
                      ].join(" ")}
                    >
                      {s.icon ?? <DotIcon />}
                    </span>
                    <span className="flex-1 truncate">{s.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-cream-200 text-center">
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
              setOpen(false);
            }}
            className="text-xs uppercase tracking-[0.18em] text-plum-mute hover:text-rose"
          >
            ↑ Back to top
          </button>
        </div>
      </aside>
    </>
  );
}

function DotIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10">
      <circle cx="5" cy="5" r="3" fill="currentColor" />
    </svg>
  );
}

// Curated little glyphs for the section list — keeps things scannable.
export const SectionIcons = {
  hero: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M5 4.5C5 3.67 5.67 3 6.5 3h11c.83 0 1.5.67 1.5 1.5v15.5h-12.5c-.83 0-1.5-.67-1.5-1.5V4.5z" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  ),
  progress: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M4 18V8M10 18v-6M16 18V4M22 18H2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  context: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.3" />
      <path d="M12 8v4.5M12 16.01V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  warnings: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M12 3L2 20h20L12 3z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M12 10v4M12 17.01V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  themes: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M12 3l2 6h6l-5 3.5 2 6.5-5-3.5-5 3.5 2-6.5L4 9h6z" fill="currentColor" opacity="0.85" />
    </svg>
  ),
  passages: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M7 7v4c0 2-1 4-3 5M17 7v4c0 2-1 4-3 5M7 7c0-1 1-2 2-2M17 7c0-1 1-2 2-2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  chapters: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M4 6h16M4 12h16M4 18h11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  notes: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M5 4h14v16H5zM9 8h6M9 12h6M9 16h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  questions: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.3" />
      <path d="M10 9.5C10 8 11 7 12 7s2 1 2 2.5-2 1.5-2 3.5M12 16.01V16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ),
  characters: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <circle cx="9" cy="9" r="3" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="16" cy="11" r="2.5" stroke="currentColor" strokeWidth="1.3" />
      <path d="M3 19c0-3 3-5 6-5s6 2 6 5M14 19c0-2.5 2-4 4.5-4S22 16.5 22 19" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  relationships: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <circle cx="6" cy="6" r="2" fill="currentColor" />
      <circle cx="18" cy="6" r="2" fill="currentColor" />
      <circle cx="12" cy="18" r="2" fill="currentColor" />
      <path d="M7.5 7.5l8 0M7 7.5l4 8.5M17 7.5l-4 8.5" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.7" />
    </svg>
  ),
  venue: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M12 2c-4 0-7 3-7 7 0 5 7 12 7 12s7-7 7-12c0-4-3-7-7-7z" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="12" cy="9" r="2.5" fill="currentColor" />
    </svg>
  ),
  merch: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M5 7l1 12h12l1-12M5 7l3-4h8l3 4M5 7h14M10 11v3M14 11v3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  pairings: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M7 3h10l-1 7c0 2-2 4-4 4s-4-2-4-4l-1-7zM12 14v6M9 20h6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  ratings: (
    <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
      <path d="M12 3l2.5 6.5L21 10l-5 4 1.5 7-5.5-3.5L6.5 21 8 14l-5-4 6.5-0.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  ),
};
